import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import {
    History, Calendar, IndianRupee, Search,
    ChevronLeft, ChevronRight, Eye, X,
    CheckCircle, Clock, TrendingUp,
    Users, ArrowDownRight, ArrowUpRight,
    Filter, RefreshCw
} from 'lucide-react';
import API_BASE_URL from '../../config/api';

const PayoutHistory = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [availableDates, setAvailableDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [viewDetail, setViewDetail] = useState(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const fetchHistory = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const url = new URL(`${API_BASE_URL}/admin/payout_history.php`);
            if (fromDate) url.searchParams.append('from', fromDate);
            if (toDate) url.searchParams.append('to', toDate);
            if (!fromDate && !toDate) url.searchParams.append('date', selectedDate);
            url.searchParams.append('_t', Date.now());

            const response = await fetch(url.toString(), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setData(result.data || []);
                setSummary(result.summary || null);
                setAvailableDates(result.available_dates || []);
            }
        } catch (err) {
            console.error('Failed to fetch payout history', err);
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate, selectedDate]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    // Navigate to previous/next date
    const changeDate = (direction) => {
        const current = new Date(selectedDate);
        current.setDate(current.getDate() + direction);
        setSelectedDate(current.toISOString().split('T')[0]);
    };

    // Filter data
    const filteredData = data.filter(item => {
        const matchesSearch = searchTerm === '' ||
            (item.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.referral_code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.email || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <History className="text-purple-600" size={28} />
                            Payout History
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Daily processed payout records
                        </p>
                    </div>
                </div>

                {/* Date Navigation + Filters */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 md:p-6">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                        {/* Date Picker Range */}
                        <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
                            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                                <Calendar size={16} className="text-gray-400" />
                                <input
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    className="bg-transparent text-xs font-black focus:outline-none cursor-pointer"
                                />
                            </div>
                            <span className="text-gray-300 text-xs font-black">to</span>
                            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                                <Calendar size={16} className="text-gray-400" />
                                <input
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    className="bg-transparent text-xs font-black focus:outline-none cursor-pointer"
                                />
                            </div>

                            <button
                                onClick={fetchHistory}
                                className="px-4 py-2 bg-purple-600 text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-purple-700 transition-all shadow-lg shadow-purple-100 flex items-center gap-2"
                            >
                                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                                Filter
                            </button>

                            <div className="h-8 w-px bg-gray-100 hidden lg:block"></div>

                            <div className="flex bg-gray-50 p-1 rounded-xl border border-gray-100 items-center">
                                <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-100 shadow-sm cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                                    <Calendar size={14} className="text-purple-600" />
                                    <input
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) => { setFromDate(''); setToDate(''); setSelectedDate(e.target.value); }}
                                        className="bg-transparent text-xs font-black focus:outline-none cursor-pointer"
                                    />
                                </div>
                                <button
                                    onClick={() => { setFromDate(''); setToDate(''); changeDate(-1); }}
                                    className="p-2 hover:bg-white hover:shadow-sm rounded-lg transition-all ml-1 group"
                                    title="Previous Day"
                                >
                                    <ChevronLeft size={18} className="text-gray-400 group-hover:text-purple-600" />
                                </button>
                                <button
                                    onClick={() => { setFromDate(''); setToDate(''); changeDate(1); }}
                                    className="p-2 hover:bg-white hover:shadow-sm rounded-lg transition-all group"
                                    title="Next Day"
                                >
                                    <ChevronRight size={18} className="text-gray-400 group-hover:text-purple-600" />
                                </button>
                            </div>
                        </div>

                        {/* Search + Status Filter */}
                        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                            <div className="relative flex-1 sm:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    placeholder="Search by name, ID, email..."
                                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none transition-all shadow-sm"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="relative sm:w-44">
                                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <select
                                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none appearance-none cursor-pointer shadow-sm font-medium"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">All Status</option>
                                    <option value="paid">Paid</option>
                                    <option value="pending">Pending</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Quick Date Selector for available dates */}
                    {availableDates.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Quick Select (Recent Dates with Payouts)</p>
                            <div className="flex flex-wrap gap-2">
                                {availableDates.slice(0, 14).map((d, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setSelectedDate(d.payout_date)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${selectedDate === d.payout_date
                                            ? 'bg-purple-600 text-white border-purple-600 shadow-md shadow-purple-200'
                                            : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300 hover:text-purple-600'
                                            }`}
                                    >
                                        {formatDate(d.payout_date)}
                                        <span className="ml-1 opacity-60">({d.record_count})</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Summary Cards */}
                {summary && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl p-4 text-white shadow-xl col-span-2 md:col-span-1">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-1.5 bg-white/20 rounded-lg">
                                    <IndianRupee size={16} />
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-purple-100">Total Gross</span>
                            </div>
                            <p className="text-2xl font-black">{formatCurrency(summary.total_gross_payout)}</p>
                        </div>

                        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-1.5 bg-green-50 text-green-600 rounded-lg">
                                    <TrendingUp size={16} />
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Matching</span>
                            </div>
                            <p className="text-lg font-black text-gray-900">{formatCurrency(summary.total_matching_income)}</p>
                        </div>

                        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
                                    <ArrowUpRight size={16} />
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Net Payout</span>
                            </div>
                            <p className="text-lg font-black text-gray-900">{formatCurrency(summary.total_net_payout)}</p>
                        </div>

                        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-1.5 bg-red-50 text-red-600 rounded-lg">
                                    <ArrowDownRight size={16} />
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Deductions</span>
                            </div>
                            <p className="text-lg font-black text-gray-900">{formatCurrency(summary.total_admin_fee + summary.total_tds)}</p>
                        </div>

                        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg">
                                    <CheckCircle size={16} />
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Paid</span>
                            </div>
                            <p className="text-lg font-black text-emerald-600">{summary.paid_count}</p>
                        </div>

                        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-1.5 bg-yellow-50 text-yellow-600 rounded-lg">
                                    <Clock size={16} />
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Pending</span>
                            </div>
                            <p className="text-lg font-black text-yellow-600">{summary.pending_count}</p>
                        </div>
                    </div>
                )}

                {/* Data Table */}
                <div className="bg-white rounded-2xl shadow-xl shadow-purple-100/20 border border-gray-100 overflow-hidden">
                    <div className="p-4 md:p-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">
                                Payout Records — {formatDate(selectedDate)}
                            </h3>
                            <p className="text-sm text-gray-500 mt-0.5">
                                {filteredData.length} record{filteredData.length !== 1 ? 's' : ''} found
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${summary?.total_records > 0
                                ? 'bg-green-50 text-green-700 border border-green-100'
                                : 'bg-gray-50 text-gray-500 border border-gray-100'
                                }`}>
                                <Users size={12} />
                                {summary?.total_records || 0} Total
                            </span>
                        </div>
                    </div>

                    <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gradient-to-r from-gray-50 to-white text-gray-500 text-[11px] uppercase tracking-wider font-bold border-b border-gray-100">
                                <tr>
                                    <th className="px-4 md:px-6 py-4">User</th>
                                    <th className="px-4 md:px-6 py-4">Cycle</th>
                                    <th className="px-4 md:px-6 py-4">Matching PV</th>
                                    <th className="px-4 md:px-6 py-4">Matching ₹</th>
                                    <th className="px-4 md:px-6 py-4">Perf Bonus</th>
                                    <th className="px-4 md:px-6 py-4">Gross</th>
                                    <th className="px-4 md:px-6 py-4">Net</th>
                                    <th className="px-4 md:px-6 py-4 text-center">Status</th>
                                    <th className="px-4 md:px-6 py-4">Time</th>
                                    <th className="px-4 md:px-6 py-4 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {loading ? (
                                    <tr>
                                        <td colSpan="10" className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-600 border-t-transparent shadow-lg shadow-purple-200"></div>
                                                <p className="text-gray-400 font-medium animate-pulse">Loading payout history...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredData.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
                                                    <History size={32} className="text-gray-300" />
                                                </div>
                                                <p className="text-gray-400 font-medium">No payout records found for {formatDate(selectedDate)}</p>
                                                <p className="text-gray-300 text-sm">Try selecting a different date</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredData.map((item, idx) => (
                                        <tr key={item.id || idx} className="hover:bg-purple-50/30 transition-colors group">
                                            <td className="px-4 md:px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold text-sm group-hover:scale-110 transition-transform">
                                                        {(item.user_name || 'U').charAt(0)}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-gray-900 text-sm group-hover:text-purple-600 transition-colors">{item.user_name}</p>
                                                        <p className="text-[10px] text-gray-400 font-mono tracking-tight uppercase">{item.referral_code}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 md:px-6 py-4">
                                                <span className="inline-flex items-center px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-bold">
                                                    #{item.cycle_number}
                                                </span>
                                            </td>
                                            <td className="px-4 md:px-6 py-4 font-medium text-gray-700">
                                                {item.matching_pv.toLocaleString('en-IN')} PV
                                            </td>
                                            <td className="px-4 md:px-6 py-4 font-bold text-gray-900">
                                                {formatCurrency(item.matching_income)}
                                            </td>
                                            <td className="px-4 md:px-6 py-4">
                                                {item.performance_bonus > 0 ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-bold">
                                                        <TrendingUp size={10} />
                                                        {formatCurrency(item.performance_bonus)}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-300 text-xs">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 md:px-6 py-4 font-bold text-gray-900">
                                                {formatCurrency(item.total_payout)}
                                            </td>
                                            <td className="px-4 md:px-6 py-4">
                                                <span className="font-bold text-green-700">
                                                    {formatCurrency(item.net_payout)}
                                                </span>
                                            </td>
                                            <td className="px-4 md:px-6 py-4 text-center">
                                                {item.status === 'paid' ? (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 rounded-full text-[10px] font-black uppercase border border-green-100">
                                                        <CheckCircle size={10} />
                                                        Paid
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-50 text-yellow-700 rounded-full text-[10px] font-black uppercase border border-yellow-100">
                                                        <Clock size={10} />
                                                        Pending
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 md:px-6 py-4 text-gray-500 text-xs">
                                                {formatTime(item.created_at)}
                                            </td>
                                            <td className="px-4 md:px-6 py-4 text-right">
                                                <button
                                                    onClick={() => setViewDetail(item)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all font-bold text-xs shadow-md shadow-purple-100"
                                                >
                                                    <Eye size={12} />
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="grid grid-cols-1 gap-4 p-4 lg:hidden">
                        {loading ? (
                            <div className="py-20 text-center">
                                <div className="flex flex-col items-center gap-3">
                                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-600 border-t-transparent shadow-lg shadow-purple-200"></div>
                                    <p className="text-gray-400 font-medium animate-pulse">Loading payout history...</p>
                                </div>
                            </div>
                        ) : filteredData.length === 0 ? (
                            <div className="py-20 text-center">
                                <div className="flex flex-col items-center gap-3">
                                    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
                                        <History size={32} className="text-gray-300" />
                                    </div>
                                    <p className="text-gray-400 font-medium">No payout records found for {formatDate(selectedDate)}</p>
                                    <p className="text-gray-300 text-sm">Try selecting a different date</p>
                                </div>
                            </div>
                        ) : (
                            filteredData.map((item, idx) => (
                                <div key={item.id || idx} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col gap-4 relative">
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold text-sm">
                                                {(item.user_name || 'U').charAt(0)}
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-900">{item.user_name}</p>
                                                <p className="text-[10px] text-gray-400 font-mono tracking-tight uppercase">{item.referral_code}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-green-700">{formatCurrency(item.net_payout)}</p>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Net Payout</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 pb-2 border-b border-gray-50">
                                        <div className="bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                                            <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">Cycle</span>
                                            <span className="text-xs font-black text-indigo-700 italic uppercase">#{item.cycle_number}</span>
                                        </div>
                                        <div className="bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                                            <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">Status</span>
                                            {item.status === 'paid' ? (
                                                <span className="inline-flex items-center gap-1 text-green-700 text-xs font-black uppercase">
                                                    <CheckCircle size={10} /> Paid
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-yellow-700 text-xs font-black uppercase">
                                                    <Clock size={10} /> Pending
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between pt-1">
                                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                                            {formatTime(item.created_at)}
                                        </span>
                                        <button
                                            onClick={() => setViewDetail(item)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-all font-bold text-xs border border-purple-100"
                                        >
                                            <Eye size={12} /> View Details
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Detail Modal */}
                {viewDetail && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                        <div className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
                            {/* Modal Header */}
                            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-5 md:p-6 flex justify-between items-center text-white">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-xl font-black backdrop-blur-md">
                                        {(viewDetail.user_name || 'U').charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-black tracking-tight">{viewDetail.user_name}</h3>
                                        <p className="text-purple-100 text-xs font-bold font-mono tracking-widest">{viewDetail.referral_code}</p>
                                    </div>
                                </div>
                                <button onClick={() => setViewDetail(null)} className="p-2 bg-black/10 hover:bg-black/20 rounded-xl transition-colors">
                                    <X size={22} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-5 md:p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                                {/* Status + Cycle */}
                                <div className="flex items-center gap-4 flex-wrap">
                                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold ${viewDetail.status === 'paid'
                                        ? 'bg-green-50 text-green-700 border border-green-200'
                                        : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                                        }`}>
                                        {viewDetail.status === 'paid' ? <CheckCircle size={16} /> : <Clock size={16} />}
                                        {viewDetail.status === 'paid' ? 'Paid' : 'Pending'}
                                    </span>
                                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-xl text-sm font-bold border border-indigo-200">
                                        Cycle #{viewDetail.cycle_number}
                                    </span>
                                    {viewDetail.created_at && (
                                        <span className="text-xs text-gray-400 font-medium">
                                            {formatDate(viewDetail.created_at)} at {formatTime(viewDetail.created_at)}
                                        </span>
                                    )}
                                </div>

                                {/* PV Breakdown */}
                                <section>
                                    <h4 className="text-xs font-black text-gray-700 uppercase tracking-widest mb-3 flex items-center gap-2">
                                        <TrendingUp size={14} className="text-purple-500" />
                                        PV Breakdown
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {[
                                            { label: 'Input Left PV', value: viewDetail.input_left_pv, color: 'blue' },
                                            { label: 'Input Right PV', value: viewDetail.input_right_pv, color: 'blue' },
                                            { label: 'Prev Left CF', value: viewDetail.previous_left_cf, color: 'gray' },
                                            { label: 'Prev Right CF', value: viewDetail.previous_right_cf, color: 'gray' },
                                            { label: 'Effective Left', value: viewDetail.effective_left_pv, color: 'purple' },
                                            { label: 'Effective Right', value: viewDetail.effective_right_pv, color: 'purple' },
                                            { label: 'Matching PV', value: viewDetail.matching_pv, color: 'green' },
                                            { label: 'New Left CF', value: viewDetail.new_left_cf, color: 'indigo' },
                                            { label: 'New Right CF', value: viewDetail.new_right_cf, color: 'indigo' },
                                        ].map((item, i) => (
                                            <div key={i} className={`bg-${item.color}-50/50 p-3 rounded-xl border border-${item.color}-100`}>
                                                <p className="text-[9px] text-gray-500 font-black uppercase tracking-widest mb-1">{item.label}</p>
                                                <p className="text-lg font-black text-gray-900">{item.value?.toLocaleString('en-IN') || '0'}</p>
                                            </div>
                                        ))}
                                    </div>
                                </section>

                                {/* Financial Breakdown */}
                                <section>
                                    <h4 className="text-xs font-black text-gray-700 uppercase tracking-widest mb-3 flex items-center gap-2">
                                        <IndianRupee size={14} className="text-green-500" />
                                        Financial Breakdown
                                    </h4>
                                    <div className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-2xl border border-gray-100 space-y-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Matching Income</span>
                                            <span className="font-bold text-gray-900">{formatCurrency(viewDetail.matching_income)}</span>
                                        </div>
                                        {viewDetail.performance_bonus > 0 && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm text-gray-600">Performance Bonus</span>
                                                <span className="font-bold text-amber-600">{formatCurrency(viewDetail.performance_bonus)}</span>
                                            </div>
                                        )}
                                        {viewDetail.eligibility_bonus > 0 && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm text-gray-600">Eligibility Bonus</span>
                                                <span className="font-bold text-blue-600">{formatCurrency(viewDetail.eligibility_bonus)}</span>
                                            </div>
                                        )}
                                        <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                                            <span className="text-sm font-bold text-gray-700">Gross Payout</span>
                                            <span className="font-black text-gray-900">{formatCurrency(viewDetail.total_payout)}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-red-600">
                                            <span className="text-sm">Admin Fee</span>
                                            <span className="font-bold">- {formatCurrency(viewDetail.admin_fee)}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-red-600">
                                            <span className="text-sm">TDS</span>
                                            <span className="font-bold">- {formatCurrency(viewDetail.tds)}</span>
                                        </div>
                                        <div className="border-t-2 border-green-200 pt-3 flex justify-between items-center">
                                            <span className="text-sm font-black text-green-700">Net Payout</span>
                                            <span className="font-black text-xl text-green-700">{formatCurrency(viewDetail.net_payout)}</span>
                                        </div>
                                    </div>
                                </section>

                                {/* Contact Info */}
                                <section>
                                    <h4 className="text-xs font-black text-gray-700 uppercase tracking-widest mb-3">Contact</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                                            <p className="text-[9px] text-gray-400 font-black uppercase mb-1">Email</p>
                                            <p className="font-bold text-gray-900 text-sm truncate">{viewDetail.email}</p>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                                            <p className="text-[9px] text-gray-400 font-black uppercase mb-1">Mobile</p>
                                            <p className="font-bold text-gray-900 text-sm">{viewDetail.mobile || 'N/A'}</p>
                                        </div>
                                    </div>
                                </section>
                            </div>

                            {/* Modal Footer */}
                            <div className="bg-gray-50 p-5 border-t border-gray-100 flex justify-end">
                                <button
                                    onClick={() => setViewDetail(null)}
                                    className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-bold text-sm"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default PayoutHistory;
